import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;
const BASE_W = 6 * IN;
const BASE_D = 5 * IN;
const BASE_H = 0.75 * IN;
const ARM_W = 0.75 * IN;
const ARM_D = 0.5 * IN;
const ARM_H = 10 * IN;
const BAR_W = 14 * IN;
const BAR_D = 1.25 * IN;
const BAR_H = 0.375 * IN;
const BAR_ANGLE = (-5 * Math.PI) / 180;

export function DeskLampSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <SideView
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <FrontView
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function FrontView({ width, height, strokeColor, strokeWidth }) {
  const cx = width / 2;
  const baseTop = height - BASE_H;
  const armTop = baseTop - ARM_H;

  return (
    <g>
      {/* Base */}
      <rect
        x={cx - BASE_W / 2}
        y={baseTop}
        width={BASE_W}
        height={BASE_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Arm */}
      <rect
        x={cx - ARM_W / 2}
        y={armTop}
        width={ARM_W}
        height={ARM_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Light bar (shown as thin rectangle at top, front view = narrow) */}
      <rect
        x={cx - BAR_D / 2}
        y={armTop - BAR_H}
        width={BAR_D}
        height={BAR_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

function SideView({ depth, height, strokeColor, strokeWidth }) {
  const baseTop = height - BASE_H;
  const armBottom = baseTop;
  const armTop = armBottom - ARM_H;
  const armZ = ARM_D / 2 + 0.25 * IN;

  const barStartZ = armZ;
  const barStartY = armTop;
  const barEndZ = armZ + BAR_W * Math.cos(BAR_ANGLE);
  const barEndY = armTop + BAR_W * Math.sin(BAR_ANGLE);

  return (
    <g>
      {/* Base */}
      <rect
        x={0}
        y={baseTop}
        width={BASE_D}
        height={BASE_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Arm */}
      <rect
        x={armZ - ARM_D / 2}
        y={armTop}
        width={ARM_D}
        height={ARM_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Light bar — angled line from arm top extending forward */}
      <Line
        x1={barStartZ}
        y1={barStartY}
        x2={barEndZ}
        y2={barEndY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={barStartZ}
        y1={barStartY + BAR_H}
        x2={barEndZ}
        y2={barEndY + BAR_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Cap the end of the bar */}
      <Line
        x1={barEndZ}
        y1={barEndY}
        x2={barEndZ}
        y2={barEndY + BAR_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DeskLampSchematic.supportsSideViews = true;
